import { PlaceSummary } from '@/hooks/usePlaces';
import { PlaceCard } from './PlaceCard';
import { PlaceCardSkeleton } from './PlaceCardSkeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface NearbyPanelProps {
  places: PlaceSummary[];
  isLoading?: boolean;
  onPlaceSelect: (place: PlaceSummary) => void;
}

export const NearbyPanel = ({ places, isLoading, onPlaceSelect }: NearbyPanelProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sort by featured first, then by distance_km
  const sortedPlaces = useMemo(() => {
    return [...places].sort((a, b) => {
      // Featured places first
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      
      // Then by distance
      if (a.distance_km === null) return 1;
      if (b.distance_km === null) return -1;
      return a.distance_km - b.distance_km;
    });
  }, [places]);

  return (
    <div className={cn(
      'bg-card/95 backdrop-blur-md rounded-2xl transition-all duration-300 overflow-hidden shadow-lg border border-border/30',
      isCollapsed ? 'w-12' : 'w-80'
    )}>
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        {!isCollapsed && (
          <h2 className="font-semibold text-foreground">Lieux à proximité</h2>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Afficher le panneau' : 'Réduire le panneau'}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {!isCollapsed && (
        <div className="p-3 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <PlaceCardSkeleton key={i} />
              ))}
            </div>
          ) : sortedPlaces.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aucun lieu trouvé
            </p>
          ) : (
            sortedPlaces.slice(0, 10).map((place) => (
              <PlaceCard
                key={place.id}
                place={place}
                onClick={() => onPlaceSelect(place)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
